import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { toggleDarkMode, toggleMobileSidebar } from '../../redux/reducers/settingsSlice'
import { logoutAdmin } from '../../redux/reducers/authSlice'
import {
  Sun,
  Moon,
  Bell,
  LogOut,
  User,
  Search,
  Wifi,
  WifiOff,
  Menu,
} from 'lucide-react'

const Header: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { darkMode } = useAppSelector((state) => state.settings)
  const { user } = useAppSelector((state) => state.auth)
  const { alerts } = useAppSelector((state) => state.monitoring)
  const { isConnected } = useAppSelector((state) => state.socket)
  
  const unreadAlerts = alerts.filter(a => !a.resolved).length
  
  const handleLogout = () => {
    dispatch(logoutAdmin())
    navigate('/login')
  }
  
  return (
    <header className="h-16 bg-card border-b border-border px-3 sm:px-6 flex items-center justify-between">
      {/* Mobile Menu Button and Search Bar */}
      <div className="flex items-center flex-1 max-w-xl gap-3">
        {/* Hamburger Menu - Mobile Only */}
        <button
          onClick={() => dispatch(toggleMobileSidebar())}
          className="md:hidden p-2 hover:bg-muted rounded-lg transition-colors"
          aria-label="Toggle menu"
        >
          <Menu className="w-5 h-5 text-muted-foreground" />
        </button>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search..."
            className="h-9 w-full pl-10 pr-3 bg-muted/50 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent placeholder:text-xs sm:placeholder:text-sm"
          />
        </div>
      </div>
      
      {/* Right Actions */}
      <div className="flex items-center space-x-2 sm:space-x-3">
        {/* Connection Status */}
        <button
          className="p-1.5 hover:bg-muted rounded-md transition-colors"
          title={isConnected ? 'Connected' : 'Disconnected'}
        >
          {isConnected ? (
            <Wifi className="w-4 h-4 text-green-500" />
          ) : (
            <WifiOff className="w-4 h-4 text-red-500" />
          )}
        </button>
        
        {/* Dark Mode Toggle */}
        <button
          onClick={() => dispatch(toggleDarkMode())}
          className="p-1.5 hover:bg-muted rounded-md transition-colors"
          title={darkMode ? 'Light mode' : 'Dark mode'}
        >
          {darkMode ? (
            <Sun className="w-4 h-4 text-muted-foreground" />
          ) : (
            <Moon className="w-4 h-4 text-muted-foreground" />
          )}
        </button>
        
        {/* Notifications */}
        <button
          className="relative p-2 hover:bg-muted rounded-lg transition-colors"
          title="Notifications"
        >
          <Bell className="w-4 h-4 text-muted-foreground" />
          {unreadAlerts > 0 && (
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          )}
        </button>
        
        {/* User Menu */}
        <div className="flex items-center space-x-2 pl-2 sm:pl-3 border-l border-border">
          <div className="text-right hidden sm:block">
            <p className="text-sm font-medium text-foreground">{user?.name || user?.email || 'Admin'}</p>
            <p className="text-xs text-muted-foreground">{user?.role || 'admin'}</p>
          </div>
          
          <div className="flex items-center space-x-2">
            <button className="p-1.5 hover:bg-muted rounded-md transition-colors">
              <User className="w-4 h-4 text-muted-foreground" />
            </button>
            
            <button
              onClick={handleLogout}
              className="p-1.5 hover:bg-muted rounded-md transition-colors"
              title="Logout"
            >
              <LogOut className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header